import type { Conversation, Message, Paginator } from '@twilio/conversations';
import type { IMessage, AvatarProps, BubbleProps, SystemMessageProps } from 'react-native-gifted-chat';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { ActivityIndicator } from 'react-native';

import { GiftedChat, Bubble } from 'react-native-gifted-chat';
import { YStack } from 'tamagui';

import { Toast } from '@shared/utils';
import { BodyS, LabelM } from '@south-street-app/atoms';
import { InitialsCircle } from '@south-street-app/molecules';
import { useChatStore } from '@south-street-app/stores';
import {
  getConversationAttributes,
  isVisibleConversationMessage,
  markConversationRead,
} from '@south-street-app/utils';
import { useGetCurrentEndUser } from '@utility-nyc/react-query-sdk';

import { ChatInput } from '../ChatScreen/ChatInput';
import { ChatSendButton } from '../ChatScreen/ChatSendButton';

const PAGE_SIZE = 30;

const toGiftedChatMessage = (message: Message): IMessage | null => {
  if (!isVisibleConversationMessage(message.author, message.body)) {
    return null;
  }

  return {
    _id: message.sid,
    text: message.body ?? '',
    createdAt: message.dateCreated ?? new Date(),
    user: {
      _id: message.author ?? '',
      name: message.author ?? '',
    },
  };
};

const mergeMessages = (messages: IMessage[]) =>
  [...messages].sort(
    (left, right) =>
      new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime(),
  );

const RESOLVED_MESSAGE_ID = 'resolved-indicator';

const ResolvedSystemMessage = (_props: SystemMessageProps<IMessage>) => (
  <YStack alignItems={'center'} paddingVertical={'$3'}>
    <BodyS color={'$mono500'}>{"Marked as resolved"}</BodyS>
  </YStack>
);

const GenericChatAvatar = (props: AvatarProps<IMessage>) => {
  if (props.position !== 'left') {
    return null;
  }

  return (
    <YStack marginRight={'$2'}>
      <InitialsCircle name={'South Street'} size={'small'} usePadding={false} />
    </YStack>
  );
};

const GenericChatBubble = (props: BubbleProps<IMessage>) => (
  <Bubble
    {...props}
    renderTime={() => null}
    textStyle={{
      left: { color: '#0E1116', fontFamily: 'Inter-Regular' },
      right: { color: '#FFFFFF', fontFamily: 'Inter-Regular' },
    }}
    wrapperStyle={{
      left: { backgroundColor: '#FFFFFF', padding: 16 },
      right: { backgroundColor: '#1B3B8C', padding: 16 },
    }}
  />
);

type ConversationChatViewProps = {
  conversation: Conversation | null;
  emptyMessage: string;
  hideSenderIdentity?: boolean;
  inlineTitle?: string;
  onSendMessage?: (message: string) => Promise<void>;
  placeholder?: string;
  readOnly?: boolean;
};

const ConversationChatView = ({
  conversation,
  emptyMessage,
  hideSenderIdentity = false,
  inlineTitle,
  onSendMessage,
  placeholder = 'Message',
  readOnly = false,
}: ConversationChatViewProps) => {
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  const [sending, setSending] = useState(false);
  const [paginator, setPaginator] = useState<Paginator<Message> | null>(null);
  const lockChat = useChatStore(
    (state) => state.lockChat,
  );
  const { data: currentUser } = useGetCurrentEndUser();

  const composerDisabled = readOnly || lockChat || sending;
  const activeUserId = useMemo(
    () => `END_USER:${currentUser?.data.id ?? ''}`,
    [currentUser?.data.id],
  );

  const displayMessages = useMemo(() => {
    const attributes = getConversationAttributes(conversation);

    if (attributes.status !== 'resolved') {
      return messages;
    }

    const resolvedMessage: IMessage = {
      _id: RESOLVED_MESSAGE_ID,
      text: 'Marked as resolved',
      system: true,
      createdAt: messages[0]?.createdAt ?? new Date(),
      user: { _id: 'system' },
    };

    return [resolvedMessage, ...messages];
  }, [conversation, messages]);

  useEffect(() => {
    let isMounted = true;

    const loadMessages = async () => {
      if (!conversation) {
        setMessages([]);

        setPaginator(null);

        setLoading(false);

        return;
      }

      setLoading(true);

      try {
        const nextPaginator = await conversation.getMessages(PAGE_SIZE);

        if (!isMounted) {
          return;
        }

        setPaginator(nextPaginator);

        setMessages(
          mergeMessages(
            nextPaginator.items
              .map((message) => toGiftedChatMessage(message))
              .filter((message): message is IMessage => Boolean(message)),
          ),
        );
      } catch {
        if (isMounted) {
          setMessages([]);

          setPaginator(null);

          Toast.showErrorToast({
            text1: 'Could not load messages',
            text2: 'Please try again.',
          });
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadMessages().catch(() => undefined);

    return () => {
      isMounted = false;
    };
  }, [conversation]);

  useEffect(() => {
    markConversationRead(conversation).catch(() => undefined);
  }, [conversation]);

  useEffect(() => {
    if (!conversation) {
      return;
    }

    const handleMessageAdded = (message: Message) => {
      const nextMessage = toGiftedChatMessage(message);

      if (!nextMessage) {
        return;
      }

      setMessages((currentMessages) => {
        if (
          currentMessages.some(
            (currentMessage) => currentMessage._id === nextMessage._id,
          )
        ) {
          return currentMessages;
        }

        return mergeMessages([nextMessage, ...currentMessages]);
      });

      markConversationRead(conversation).catch(() => undefined);
    };

    conversation.on('messageAdded', handleMessageAdded);

    return () => {
      conversation.removeListener('messageAdded', handleMessageAdded);
    };
  }, [conversation]);

  const onLoadEarlier = useCallback(async () => {
    if (!paginator?.hasNextPage) {
      return;
    }

    setLoadingEarlier(true);

    try {
      const nextPaginator = await paginator.nextPage();

      setPaginator(nextPaginator);

      setMessages((currentMessages) =>
        mergeMessages([
          ...currentMessages,
          ...nextPaginator.items
            .map((message) => toGiftedChatMessage(message))
            .filter((message): message is IMessage => Boolean(message)),
        ]),
      );
    } catch {
      Toast.showErrorToast({
        text1: 'Could not load earlier messages',
        text2: 'Please try again.',
      });
    } finally {
      setLoadingEarlier(false);
    }
  }, [paginator]);

  const onSend = useCallback(
    async (nextMessages: IMessage[] = []) => {
      const nextMessage = nextMessages[0]?.text?.trim();

      if (!conversation || !nextMessage || !onSendMessage) {
        return;
      }

      setSending(true);

      try {
        await onSendMessage(nextMessage);
      } catch {
        Toast.showErrorToast({
          text1: 'Could not send message',
          text2: 'Please try again.',
        });
      } finally {
        setSending(false);
      }
    },
    [conversation, onSendMessage],
  );

  if (loading) {
    return (
      <YStack alignItems={'center'} flex={1} justifyContent={'center'}>
        <ActivityIndicator size={'large'} />
      </YStack>
    );
  }

  if (!conversation) {
    return (
      <YStack alignItems={'center'} flex={1} gap={'$2'} justifyContent={'center'} padding={'$6'}>
        {inlineTitle ? (
          <LabelM color={'$mono900'} fontWeight={'$2'}>
            {inlineTitle}
          </LabelM>
        ) : null}
        <BodyS color={'$mono500'} textAlign={'center'}>
          {emptyMessage}
        </BodyS>
      </YStack>
    );
  }

  return (
    <YStack flex={1}>
      {inlineTitle ? (
        <YStack
          backgroundColor={'$mono100'}
          borderBottomColor={'$mono300'}
          borderBottomWidth={1}
          paddingHorizontal={'$4'}
          paddingVertical={'$3'}
        >
          <LabelM color={'$mono900'} fontWeight={'$2'}>
            {inlineTitle}
          </LabelM>
        </YStack>
      ) : null}

      <GiftedChat
        alwaysShowSend={!composerDisabled}
        loadEarlier={paginator?.hasNextPage ?? false}
        messages={displayMessages}
        onLoadEarlier={onLoadEarlier}
        onSend={onSend}
        renderAvatar={hideSenderIdentity ? () => null : GenericChatAvatar}
        renderBubble={GenericChatBubble}
        renderDay={() => null}
        renderInputToolbar={(props) =>
          readOnly ? null : <ChatInput {...props} />
        }
        renderLoading={() => <ActivityIndicator size={'small'} />}
        renderSend={(props) =>
          composerDisabled ? null : <ChatSendButton {...props} />
        }
        renderSystemMessage={ResolvedSystemMessage}
        renderTime={() => null}
        renderUsername={() => null}
        textInputProps={{ editable: !composerDisabled, placeholder: lockChat ? 'Messaging is currently unavailable.' : placeholder }}
        user={{
          _id: activeUserId,
          name: activeUserId,
        }}
        isLoadingEarlier={loadingEarlier}
      />
    </YStack>
  );
};

export { ConversationChatView };
